"use client";

import { ReactNode, useMemo } from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import FormProvider from "@/libs/shared/providers/FormProvider";
import { SignupStartModel } from "@/libs/auth/redux/signup.types";
import { useSignupContext } from "@/libs/auth/providers/SignupProvider";

type SignupStartProviderProps = {
  children: ReactNode;
};

const SignupStartProvider = ({ children }: SignupStartProviderProps) => {
  const { signupForm } = useSignupContext();
  const { t } = useTranslation();

  const validationSchema = useMemo(
    () =>
      yup.object<SignupStartModel>().shape({
        email: yup
          .string()
          .email(t("common:error:email") || "")
          .required(t("common:error:required") || ""),
        password: yup
          .string()
          .min(6, t("common:error:min_length") || "")
          .required(t("common:error:required") || ""),
        gdpr_terms: yup
          .boolean()
          .required(t("common:error:required") || "")
          .oneOf([true], t("common:error:required") || ""),
        marketing: yup.boolean().required(t("common:error:required") || ""),
        referral_code: yup.string().optional(),
        language: yup.string().optional().default("it"),
      }),
    [t],
  );

  const options = useMemo(
    () => ({
      resolver: yupResolver(validationSchema) as any,
      mode: "onChange" as const,
      defaultValues: {
        email: signupForm.email || "",
        password: signupForm.password || "",
        gdpr_terms: signupForm.gdpr_terms || false,
        marketing: signupForm.marketing || false,
        referral_code: signupForm.referral_code || "",
      },
    }),
    [validationSchema, signupForm],
  );

  return <FormProvider {...options}>{children}</FormProvider>;
};

export default SignupStartProvider;
